import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { sendContactEmail } from '../api/contact';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('sending');
    try {
      await sendContactEmail(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    }
  };

  return (
    <div className="py-16 bg-black relative overflow-hidden">
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gold-950/30 to-black/90"
        animate={{ 
          backgroundPosition: ['center top', 'center bottom', 'center top'],
        }}
        transition={{ 
          duration: 25, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        style={{ 
          backgroundSize: 'auto 200%',
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="max-w-4xl mx-auto text-center mb-12"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gold-400 mb-6">Get in Touch</h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">Have questions about Setora, our AI technology, or investment opportunities? Reach out to us, and we'll respond as soon as possible.</p>
        </motion.div>

        <motion.div 
          className="max-w-2xl mx-auto bg-gray-900/80 border border-gold-800/30 rounded-3xl shadow-xl shadow-gold-900/20 backdrop-blur-sm p-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {submitStatus === 'success' ? (
            <motion.div 
              className="text-center p-6 bg-gold-900/30 rounded-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-xl font-bold text-gold-400 mb-2">Thank You!</h3>
              <p className="text-gray-300">Your message has been sent successfully. We'll get back to you soon.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {submitStatus === 'error' && (
                <motion.div 
                  className="p-4 bg-red-500/20 border border-red-500/40 rounded-2xl text-red-200"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  Something went wrong. Please try again later or email us directly at info@setora.ai.
                </motion.div>
              )}
              <div>
                <label htmlFor="name" className="block text-gold-400 font-medium mb-1">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gold-800/30 rounded-2xl text-gray-200 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50 transition-all"
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-gold-400 font-medium mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gold-800/30 rounded-2xl text-gray-200 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50 transition-all"
                  placeholder="Your Email"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-gold-400 font-medium mb-1">Company (Optional)</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gold-800/30 rounded-2xl text-gray-200 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50 transition-all"
                  placeholder="Your Company"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-gold-400 font-medium mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gold-800/30 rounded-2xl text-gray-200 focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50 transition-all"
                  placeholder="How can we help you?"
                ></textarea>
              </div>
              <motion.button
                type="submit"
                disabled={submitStatus === 'sending'}
                className={`w-full py-3 px-6 font-semibold rounded-2xl transition-all duration-300 ${submitStatus === 'sending' ? 'bg-gold-400/50 text-gray-300 cursor-not-allowed' : 'bg-gold-400 hover:bg-gold-300 text-black shadow-lg shadow-gold-500/30 hover:shadow-gold-400/40 pulse-glow'}`}
                whileHover={{ scale: submitStatus === 'sending' ? 1 : 1.05 }}
                whileTap={{ scale: submitStatus === 'sending' ? 1 : 0.95 }}
              >
                {submitStatus === 'sending' ? 'Sending...' : 'Send Message'}
              </motion.button>
            </form>
          )}
        </motion.div>
      </div>
      <motion.div 
        className="absolute top-0 left-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl"
        animate={{ 
          x: [0, -30, 0],
          y: [0, 30, 0],
        }}
        transition={{ 
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />
    </div>
  );
};

export default Contact; 